import { getApiUrl } from '../config';

// Re-export getApiUrl for convenience
export { getApiUrl };

interface ApiResponse<T = any> {
  data?: T;
  error?: string;
  code?: string;
}

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: any;
  headers?: Record<string, string>;
  requireAuth?: boolean;
}

/**
 * Utilidad para hacer peticiones HTTP con manejo automático de tokens expirados
 */
export const apiRequest = async <T = any>(
  endpoint: string, 
  options: ApiOptions = {}
): Promise<ApiResponse<T>> => {
  const {
    method = 'GET',
    body,
    headers = {},
    requireAuth = true
  } = options;

  try {
    // Obtener token si se requiere autenticación
    let authHeaders = { ...headers };
    if (requireAuth) {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return { error: 'No hay token de autenticación', code: 'NO_TOKEN' };
      }
      authHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Configurar headers por defecto
    if (body && !authHeaders['Content-Type']) {
      authHeaders['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      method,
      headers: authHeaders,
    };

    if (body) {
      config.body = JSON.stringify(body);
    }

    const response = await fetch(getApiUrl(endpoint), config);
    const responseData = await response.json().catch(() => ({}));

    if (response.ok) {
      return { data: responseData };
    }

    // Manejar errores de autenticación
    if (response.status === 401) {
      if (responseData.code === 'TOKEN_EXPIRED' || responseData.code === 'MALFORMED_TOKEN') {
        // Token expirado o malformado, limpiar sesión
        localStorage.removeItem('authToken');
        
        // Opcional: redirigir a login o mostrar mensaje
        // Token expirado o malformado, sesión limpiada
        
        // Puedes agregar aquí lógica para redirigir al usuario
        // window.location.href = '/auth';
      }
      
      return { 
        error: responseData.error || 'Error de autenticación', 
        code: responseData.code || 'AUTH_ERROR' 
      };
    }

    // Otros errores
    return { 
      error: responseData.error || 'Error del servidor', 
      code: responseData.code || 'SERVER_ERROR' 
    };

  } catch (error) {
    // Handle error appropriately instead of console.error
    return { 
      error: 'Error de conexión', 
      code: 'NETWORK_ERROR' 
    };
  }
};

/**
 * Utilidad para verificar si un token está expirado
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch {
    return true; // Si no se puede decodificar, asumir que está expirado
  }
};

/**
 * Utilidad para obtener el tiempo restante del token
 */
export const getTokenTimeRemaining = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return Math.max(0, payload.exp - currentTime);
  } catch {
    return 0;
  }
};

/**
 * Objeto apiUtils con métodos convenientes para HTTP
 */
export const apiUtils = {
  get: <T = any>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'GET' }),

  post: <T = any>(endpoint: string, body?: any) =>
    apiRequest<T>(endpoint, { method: 'POST', body }),

  put: <T = any>(endpoint: string, body?: any) =>
    apiRequest<T>(endpoint, { method: 'PUT', body }),

  delete: <T = any>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'DELETE' }),

  patch: <T = any>(endpoint: string, body?: any) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body }),

  baseURL: getApiUrl(''),
};
